import React, { useRef, useState } from 'react';
import { shape, string, number, bool, arrayOf, func } from 'prop-types';
import { Icon } from '@magalu/stereo-ui-icons';
import { sortCategoriesList, useHideOverflownElements } from '../../utils';
import { Link, NavMenuContainer, NavSubmenu, NavMenu, NavMenuItem, MenuOverlay } from './links-menu.styles';
import Flex from '../../atoms/Flex';
import MenuContent from '../../molecules/MenuContent';
export const hasSubMenuContent = menuItem => {
  const {
    banner,
    subcategoriesList
  } = menuItem || {};
  const {
    component
  } = banner || {};
  const {
    props
  } = component || {};
  const {
    static: staticProps
  } = props || {};
  const hasBannerPath = Boolean(staticProps == null ? void 0 : staticProps.path);
  const hasBannerSlot = Boolean(staticProps == null ? void 0 : staticProps.slot);
  const hasSubcategories = Array.isArray(subcategoriesList) && subcategoriesList.length > 0;
  return hasBannerPath || hasBannerSlot || hasSubcategories;
};

const LinksMenu = ({
  data,
  enableHoverDelay,
  onMenuItemClick,
  onHoverMenuItem,
  onClickMenuItem
}) => {
  const {
    categoryList,
    specialLinks,
    externalLinks,
    allCategories
  } = data;
  const {
    orderBy = [],
    list = [],
    show
  } = categoryList;
  const categList = sortCategoriesList(list, orderBy);
  const categItems = categList.slice(0, show || orderBy.length);
  const ctnMenuRef = useRef(null);
  const menuItemRefs = {};
  const subMenuItemRefs = {};
  const [submenuMargin, setSubmenuMargin] = useState(0);
  const [selectedSubmenu, setSelectSubmenu] = useState(null);
  const [submenuOpenOrigin, setSubmenuOpenOrigin] = useState(null);
  useHideOverflownElements(menuItemRefs, ctnMenuRef, 'inline-block');

  const onItemMouseOver = key => {
    var _subMenuItemRefs$key;

    const subMenuEl = (_subMenuItemRefs$key = subMenuItemRefs[key]) == null ? void 0 : _subMenuItemRefs$key.current;

    if (subMenuEl) {
      subMenuEl.style.display = 'block';
      const subMenuWidth = subMenuEl.clientWidth;
      const itemOffset = menuItemRefs[key].current.offsetLeft;
      const headerWidth = ctnMenuRef.current.offsetWidth + ctnMenuRef.current.offsetLeft;
      const diff = itemOffset + subMenuWidth - headerWidth;
      setSubmenuMargin(diff >= 0 ? -diff : 0);
      subMenuEl.style.display = null;
    }
  };

  const handleSubMenu = (action, menu, key, origin = 'mouse') => {
    const shouldHandleSubMenu = hasSubMenuContent(menu);
    if (!shouldHandleSubMenu) return;

    if (action === 'open' && enableHoverDelay) {
      onItemMouseOver(key);
      setSelectSubmenu(key);
      setSubmenuOpenOrigin(origin);
    } else if (action === 'close') {
      setSelectSubmenu(null);
      setSubmenuOpenOrigin(null);
    }
  };

  const handleKeyDown = (e, menu, key) => {
    if (e.currentTarget !== e.target) return;
    const hasSubMenu = hasSubMenuContent(menu);

    if (e.key === 'Enter') {
      if (hasSubMenu) {
        handleSubMenu('open', menu, key, 'keyboard');
      } else {
        return;
      }
    }

    if (e.key === 'Escape' && selectedSubmenu === key) {
      var _menuItemRefs$key;

      e.preventDefault();
      handleSubMenu('close', menu, key);

      if ((_menuItemRefs$key = menuItemRefs[key]) != null && _menuItemRefs$key.current) {
        menuItemRefs[key].current.focus();
      }
    }
  };

  const renderSubmenu = (submenuData = {}, key) => {
    const handleSubmenuBlur = e => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        handleSubMenu('close', submenuData, key);
      }
    };

    return /*#__PURE__*/React.createElement(NavSubmenu, {
      marginLeft: submenuMargin,
      show: selectedSubmenu === key,
      "data-testid": "nav-submenu",
      ref: subMenuItemRefs[key],
      onBlur: handleSubmenuBlur,
      tabIndex: -1,
      onKeyDown: e => {
        if (e.key === 'Escape' && selectedSubmenu === key) {
          var _menuItemRefs$key2;

          e.preventDefault();
          handleSubMenu('close', submenuData, key);

          if ((_menuItemRefs$key2 = menuItemRefs[key]) != null && _menuItemRefs$key2.current) {
            menuItemRefs[key].current.focus();
          }
        }
      }
    }, /*#__PURE__*/React.createElement(MenuContent, {
      data: submenuData,
      onMenuItemClick: (e, link, menuType) => {
        onMenuItemClick(e, link, submenuData.label, menuType);
        handleSubMenu('close', submenuData, key);
      }
    }));
  };

  const renderMenu = ({
    menuType,
    index,
    menu,
    icon,
    chevronDown = false
  }) => {
    const {
      color,
      label,
      url
    } = menu;
    const key = `${menuType}-item-${index}`;
    menuItemRefs[key] = useRef(null);
    subMenuItemRefs[key] = useRef(null);
    const hasSubMenu = hasSubMenuContent(menu);
    return /*#__PURE__*/React.createElement(NavMenuItem, {
      tabIndex: 0,
      selected: hasSubMenu && selectedSubmenu === key,
      "data-testid": "link-menu-item",
      key: key,
      colorOnHover: hasSubMenu ? '' : color,
      onMouseEnter: event => {
        event.stopPropagation();

        if (submenuOpenOrigin !== 'keyboard') {
          handleSubMenu('open', menu, key, 'mouse');
        }

        onHoverMenuItem(menu);
      },
      onMouseLeave: () => {
        if (submenuOpenOrigin === 'mouse') {
          handleSubMenu('close', menu, key);
        }
      },
      onKeyDown: e => handleKeyDown(e, menu, key),
      ref: menuItemRefs[key]
    }, /*#__PURE__*/React.createElement(Flex, {
      mx: "2px",
      justifyContent: "center",
      alignItems: "center"
    }, icon && /*#__PURE__*/React.createElement(Icon, {
      name: icon,
      "data-testid": `link-icon-${index}`,
      color: "text.lightest"
    }), /*#__PURE__*/React.createElement(Link, {
      href: url,
      tabIndex: 0,
      onClick: event => {
        event.stopPropagation();
        onClickMenuItem(label);
      },
      onKeyDown: e => {
        if ((e.key === 'Enter' || e.keyCode === 13) && hasSubMenuContent(menu)) {
          e.preventDefault();
          handleSubMenu('open', menu, key, 'keyboard');
        }
      }
    }, label), chevronDown && /*#__PURE__*/React.createElement(Icon, {
      name: "ChevronDown",
      color: "text.lightest"
    })), renderSubmenu(menu, key));
  };

  return /*#__PURE__*/React.createElement(NavMenuContainer, {
    "data-testid": "links-menu",
    ref: ctnMenuRef
  }, /*#__PURE__*/React.createElement(NavMenu, null, renderMenu({
    menuType: 'main',
    index: 0,
    icon: 'Burger',
    menu: allCategories,
    chevronDown: true
  }), specialLinks.map((special, index) => renderMenu({
    menuType: 'special',
    index,
    menu: special
  })), categItems.map((category, index) => renderMenu({
    menuType: 'category',
    index,
    menu: category
  })), externalLinks.map((external, index) => renderMenu({
    menuType: 'external',
    index,
    menu: external,
    icon: external.id
  }))), /*#__PURE__*/React.createElement(MenuOverlay, {
    "data-testid": "menu-overlay",
    show: !!selectedSubmenu
  }));
};

process.env.NODE_ENV !== "production" ? LinksMenu.propTypes = {
  onMenuItemClick: func,
  onHoverMenuItem: func,
  onClickMenuItem: func,
  enableHoverDelay: bool,
  data: shape({
    categoryList: shape({
      order: arrayOf(string),
      show: number,
      list: arrayOf(shape({
        label: string,
        id: string,
        url: string,
        color: string,
        borderColor: string,
        banner: shape({
          img: string
        }),
        subcategoriesList: arrayOf(shape({
          id: string,
          label: string,
          url: string
        }))
      }))
    }),
    specialLinks: arrayOf(shape({
      label: string,
      id: string,
      url: string,
      color: string,
      borderColor: string,
      banner: shape({
        img: string
      })
    })),
    externalLinks: arrayOf(shape({
      label: string,
      id: string,
      url: string,
      color: string,
      borderColor: string,
      banner: shape({
        img: string
      })
    })),
    allCategories: shape({
      label: string,
      id: string,
      url: string,
      color: string,
      borderColor: string,
      banner: shape({
        img: string
      }),
      subcategoriesList: arrayOf(shape({
        id: string,
        label: string,
        url: string
      }))
    })
  })
} : void 0;
LinksMenu.defaultProps = {
  data: {
    categoryList: {},
    specialLinks: [],
    externalLinks: [],
    allCategories: {}
  },
  enableHoverDelay: true
};
export default LinksMenu;